import { Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { CurrentBenchmark } from 'src/app/models/current-benchmark.model';
import { MatTabChangeEvent } from "@angular/material/tabs";
import { StrategiesService } from 'src/app/services/strategies.service';
import { FactorStrategy } from 'src/app/models/factor-strategy.model';
import { ListsService } from 'src/app/services/lists.service';
import { StrategyType } from 'src/app/models/strategy-type.model';
import { StrategyParameters } from 'src/app/models/strategy-parameters.model';
import { ClustersService } from 'src/app/services/clusters.service';
import { Cluster } from 'src/app/models/cluster.model';
import { Router } from '@angular/router';
import { NotificationsService } from 'src/app/services/notifications.service';

@Component({
  selector: 'app-strategy-universe',
  templateUrl: './strategy-universe.component.html',
  styleUrls: ['./strategy-universe.component.scss']
})
export class StrategyUniverseComponent implements OnInit, OnDestroy {

  @Output() changeBlock = new EventEmitter<string>();

  styleBoxData: any;

  currentCluster: Cluster;
  currentClusterSubscription: Subscription;

  defaultStrategy1: FactorStrategy;
  defaultStrategy1Subscription: Subscription;
  defaultStrategy2: FactorStrategy;
  DefaultStrategy2Subscription: Subscription;
  defaultStrategy3: FactorStrategy;
  defaultStrategy3Subscription: Subscription;
  strategyBenchmark: CurrentBenchmark;
  strategyBenchmarkSubscription: Subscription;

  strategyParameters: StrategyParameters;
  strategyParametersSubscription: Subscription;

  selectedPage: string ='select';
  selectedAnalyticsTab: string = 'exposure';

  doCustomize: boolean = true;
  disableActions: boolean = false;

  selectBlock: string = '';
  panelOpenState = false;

  strategyTypeList: StrategyType[];

  constructor(private router: Router,
              private clustersService: ClustersService,
              private strategiesService: StrategiesService,
              private listsService: ListsService,
              private notificationsService: NotificationsService) {}

  ngOnInit(): void {
    this.subscribeToCurrentCluster();
    this.subscribeToDefaultStrategies();
    this.subscribeToStrategyParameters();

    this.listsService.loadLists().then(
      () => {
        this.strategyTypeList = this.listsService.getStrategyTypeList();
      }
    );
  }

  ngOnDestroy() {
    if(this.currentClusterSubscription!=undefined) { this.currentClusterSubscription.unsubscribe(); }
    if(this.defaultStrategy1Subscription!=undefined) { this.defaultStrategy1Subscription.unsubscribe(); }
    if(this.DefaultStrategy2Subscription!=undefined) { this.DefaultStrategy2Subscription.unsubscribe(); }
    if(this.defaultStrategy3Subscription!=undefined) { this.defaultStrategy3Subscription.unsubscribe(); }
    if(this.strategyBenchmarkSubscription!=undefined) { this.strategyBenchmarkSubscription.unsubscribe(); }
    if(this.strategyParametersSubscription!=undefined) { this.strategyParametersSubscription.unsubscribe(); }
  }

  subscribeToCurrentCluster() {
    this.currentClusterSubscription = this.clustersService.currentClusterSubject.subscribe(
      (cluster: Cluster) => {
        this.currentCluster = cluster;
      }
    );
    this.clustersService.emitCurrentCluster();
  }

  subscribeToDefaultStrategies() {
    this.defaultStrategy1Subscription = this.strategiesService.defaultStrategy1Subject.subscribe(
      (strategy: FactorStrategy) => {
        this.defaultStrategy1 = strategy;
        const allStrategiesLoaded = (this.defaultStrategy1!=undefined) && (this.defaultStrategy2!=undefined) && (this.defaultStrategy3!=undefined);
        if (allStrategiesLoaded) {this.computeStyleBoxData();}
      }
    );
    this.DefaultStrategy2Subscription = this.strategiesService.DefaultStrategy2Subject.subscribe(
      (strategy: FactorStrategy) => {
        this.defaultStrategy2 = strategy;
        const allStrategiesLoaded = (this.defaultStrategy1!=undefined) && (this.defaultStrategy2!=undefined) && (this.defaultStrategy3!=undefined);
        if (allStrategiesLoaded) {this.computeStyleBoxData();}
      }
    );
    this.defaultStrategy3Subscription = this.strategiesService.defaultStrategy3Subject.subscribe(
      (strategy: FactorStrategy) => {
        this.defaultStrategy3 = strategy;
        const allStrategiesLoaded = (this.defaultStrategy1!=undefined) && (this.defaultStrategy2!=undefined) && (this.defaultStrategy3!=undefined);
        if (allStrategiesLoaded) {this.computeStyleBoxData();}
      }
    );
    this.strategyBenchmarkSubscription = this.strategiesService.strategyBenchmarkSubject.subscribe(
      (benchmark: CurrentBenchmark) => {
        this.strategyBenchmark = benchmark;
      }
    );

    this.strategiesService.emitDefaultStrategy1();
    this.strategiesService.emitDefaultStrategy2();
    this.strategiesService.emitDefaultStrategy3();
    this.strategiesService.emitStrategyBenchmark();
  }

  subscribeToStrategyParameters() {
    this.strategyParametersSubscription = this.strategiesService.strategyParametersSubject.subscribe(
      (strategyParameters: StrategyParameters) => {
        this.strategyParameters = strategyParameters;
      }
    );
    this.strategiesService.emitStrategyParameters();
  }

  onAnalyticsTabChange(event: MatTabChangeEvent) {
    switch (event.tab.textLabel) {
      case 'Exposure': this.selectedAnalyticsTab='exposure'; break;
      case 'Absolute': this.selectedAnalyticsTab='absolute'; break;
      case 'Relative': this.selectedAnalyticsTab='relative'; break;
      case 'Rating':   this.selectedAnalyticsTab='rating'; break;
    }
  }

  onStrategySelection(strategyType: StrategyType) {
    this.strategiesService.resetCustomStrategy();
    this.strategiesService.changeSelectedStrategy(strategyType.alias);
  }

  async goToInvestableSolution() {
    this.notificationsService.runSpinner();
    this.disableActions = true;

    try {
      this.strategyParameters.is_customized = false;
      this.strategyParameters.customize_exposure = false;
      this.strategyParameters.customize_volatility = false;
      await this.strategiesService.computeCustomStrategy(this.strategyParameters, this.currentCluster, true);
      await this.clustersService.select(this.currentCluster.id);

      this.disableActions = false;
      this.notificationsService.stopSpinner();
      this.router.navigate(['/tools/investable-solution']);
    } catch(e) {
      this.notificationsService.notify('strategy/create-failed');
    }

    this.disableActions = false;
    this.notificationsService.stopSpinner();
  }

  computeRankings(strategy: FactorStrategy) {
    return {
      factor_deconcentration: {
        nb_analysis: strategy.factorAnalysis.rankings.factor_deconcentration_quality.nb_analysis_factor_deconcentration_quality,
        rank: strategy.factorAnalysis.rankings.factor_deconcentration_quality.rank_factor_deconcentration_quality,
        rating: strategy.factorAnalysis.diversification.factor_deconcentration_quality_rating
      },
      factor_diversification: {
        nb_analysis: strategy.factorAnalysis.rankings.factor_diversification_quality.nb_analysis_factor_diversification_quality,
        rank: strategy.factorAnalysis.rankings.factor_diversification_quality.rank_factor_diversification_quality,
        rating: strategy.factorAnalysis.diversification.factor_diversification_quality_rating
      }
    }
  }

  computeStyleBoxData() {
    this.styleBoxData = {
      default1:  this.computeRankings(this.defaultStrategy1),
      default2:  this.computeRankings(this.defaultStrategy2),
      default3: this.computeRankings(this.defaultStrategy3)
    }
  }

  onChangeBlock(selectBlock: string) {
    this.changeBlock.emit(selectBlock);
  }

}
